<template>
    <StepShell :embedded="embedded" layout="page" gap="none">
        <template v-if="generationFailed">
            <BasePageHeader
                :variant="embedded ? 'step' : 'page'"
                title="I couldn’t write your profile"
                subtitle="Your answers are saved and already shaping how I coach you — only the write-up failed. You can create it later from your profile page."
                no-border
                subtitle-below />

            <StepActions>
                <BaseButton theme="primary" :disabled="busy" @click="emit('skip')">{{ embedded ? "Continue without a profile" : "Finish without a profile" }}</BaseButton>
            </StepActions>
        </template>

        <template v-else>
            <BasePageHeader :variant="embedded ? 'step' : 'page'" title="This is what your colleagues would see" :subtitle="introCopy" no-border subtitle-below>
                <template #eyebrow><p class="eyebrow">Your collaboration profile</p></template>
            </BasePageHeader>

            <!-- Generation overwrites existing How to Work With Me content before this screen renders, so
                 disclose the replacement to users who had content.
                 -->
            <p v-if="preview.had_existing_content" class="rewritten">
                <i aria-hidden="true" class="bi bi-info-circle"></i>
                <span>I rewrote this from your answers, replacing what was here before. You can edit any section from your profile afterwards.</span>
            </p>

            <section class="document">
                <!-- Reuse the collaboration page's renderer so the preview matches published sections. Omit
                     the edit link because its destination cannot edit unpublished drafts.
                     -->
                <ProfileTopicSection :is-loading="false" :error="null" has-profile :entries="entries" :empty-profile-state="EMPTY_STATE" :empty-entries-state="EMPTY_STATE" />
            </section>

            <!-- Publishing would return 403 when private is the only allowed access level, so omit the
                 publish action.
                 -->
            <p v-if="privateOnly" class="private-only">Publishing is off for your organization, so only you can see this. I&rsquo;ll still use it when I coach you.</p>

            <p v-else class="choice-copy">
                <span v-if="profileSharerNames.length > 0">{{ sharerFirstNames }} shared theirs, which is how I could read your meetings with them. </span>{{ publishBody }} You can
                change this any time.
            </p>

            <!-- Separate action buttons prevent a selected privacy option from contradicting the submit
                 button's label.
                 -->
            <StepActions>
                <template #start>
                    <BaseButton theme="neutral" :disabled="busy" @click="emit('publish', 'private')">Keep it private</BaseButton>
                </template>
                <BaseButton v-if="publishAccess" theme="primary" :disabled="busy" @click="emit('publish', publishAccess)">Publish to my colleagues</BaseButton>
            </StepActions>
        </template>
    </StepShell>
</template>

<script setup lang="ts">
import BaseButton from "~vue/components/BaseButton.vue";
import BasePageHeader from "~vue/components/BasePageHeader.vue";
import ProfileTopicSection from "~vue/components/ProfileTopicSection.vue";
import { toProfileEntry } from "~vue/utils/profileEntries";
import { firstNameOf } from "~vue/utils/stringUtils";
import { computed } from "vue";

import StepActions from "./StepActions.vue";
import StepShell from "./StepShell.vue";
import type { AccessLevel, ProfilePreview } from "./types";

const {
    embedded = false,
    preview,
    availableAccessLevels,
    profileSharerNames,
    generationFailed,
    busy,
} = defineProps<{
    embedded?: boolean;
    preview: ProfilePreview;
    availableAccessLevels: AccessLevel[];
    profileSharerNames: string[];
    generationFailed: boolean;
    busy: boolean;
}>();

const emit = defineEmits<{ publish: [AccessLevel]; skip: [] }>();

const MAX_NAMED_SHARERS = 3;

/*
 * Generation has already created a profile here. Both renderer empty states use this message because
 * skipped questions can leave that profile without content.
 */
const EMPTY_STATE = {
    title: "Empty for now",
    body: "The questions are what fill this in. You can write it yourself from your profile at any time.",
    icon: "bi-pencil",
};

const entries = computed(() =>
    preview.subtopics
        .filter((subtopic) => subtopic.content.trim().length > 0)
        .map((subtopic) => toProfileEntry({ id: subtopic.id, title: subtopic.title, content: subtopic.content, placeholder: subtopic.placeholder })),
);

/*
 * or the choice offered here 403s on submit. Org-wide is preferred when it is
 * available; otherwise the narrower invited-members level is the real publish.
 */
const publishAccess = computed<AccessLevel | null>(() => {
    if (availableAccessLevels.includes("organization")) {
        return "organization";
    }
    if (availableAccessLevels.includes("invited_org_members")) {
        return "invited_org_members";
    }
    return null;
});

const privateOnly = computed(() => publishAccess.value === null);

const writtenCount = computed(() => preview.subtopics.filter((subtopic) => subtopic.content.length > 0).length);
const isEmpty = computed(() => writtenCount.value === 0);

/* Skipping the questions leaves every section blank, and telling that user their
   answers wrote the filled-in parts describes a screen they are not looking at. */
const introCopy = computed(() =>
    isEmpty.value
        ? "Empty for now — the questions are what fill it in, and you can write it yourself later. Nobody sees it unless you publish it."
        : "Written from your answers — nothing from your calendar goes into it. Nobody sees it until you say so.",
);

const publishBody = computed(() => {
    const access = publishAccess.value;
    if (access === null) {
        return "";
    }
    const audience = access === "organization" ? "colleagues on Nadia" : "colleagues you invite";
    const sections = writtenCount.value === 1 ? "one section" : `${writtenCount.value} sections`;
    return isEmpty.value
        ? `Publishing shares an empty profile with ${audience}.`
        : `Publishing shares the ${sections} above with ${audience} — never your calendar, never your answers.`;
});

const sharerFirstNames = computed(() =>
    profileSharerNames
        .slice(0, MAX_NAMED_SHARERS)
        .map((name) => firstNameOf(name))
        .join(", "),
);
</script>

<style scoped>
@reference "../../../css/tailwind.css";

@layer components {
    .eyebrow {
        @apply details tracking-wide text-ink-muted uppercase;
    }
    .rewritten {
        @apply body-small mb-6 flex items-start gap-2 text-ink-muted;
    }
    /* No margin of its own: the header above already ends on one, and the two
       stacked to 52px between the subtitle and the first section. */
    .document {
        @apply w-full;
    }
    .private-only {
        @apply body-small mt-6 text-ink-muted;
    }
    .choice-copy {
        @apply body-small mt-6 text-ink;
    }
}
</style>
